import { _ } from "../../../core/polyglot/polyglot.js";
import { trashAltIcon } from "../../../core/elements/icons/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import FileUploadButtonWidget from "../../../core/widgets/FileUploadButtonWidget.js";
import notifier from "../../../core/widgets/notifier/notifier.js";
import PeriodReportAttachment from "../../../models/accounting/periodReport/PeriodReportAttachment.js";
import PeriodReportAttachmentsService from "./PeriodReportAttachmentsService.js";
import PeriodReportService from "./PeriodReportService.js";
import ReportAttachmentButtonWidget from "./ReportAttachmentButtonWidget/ReportAttachmentButtonWidget.js";

/**
 * `periodReportAttachmentsEditorWidget` is used to display and edit the list of
 * attachments of a period report.
 *
 * @param {periodReport} spec.report - Report that contains the attachments
 * @param {function} spec.onUpload - Called with a file as
 *   argument. Should return a promise
 * @param {customReport[]} [spec.customReports] - customReports that
 *   can be attached
 */
export default class PeriodReportAttachmentsEditorWidget extends Widget {
	constructor({ report, onUpload, customReports }) {
		super(...arguments);

		this._report = report;
		this._onUpload = onUpload;
		this._customReports = customReports;

		this.attachmentsChanged = this._events.createEvent();
		this._service = new PeriodReportService();
	}

	setReport(report) {
		this._report = report;
		this.update();
	}

	renderContentOn(html) {
		html.div({ klass: "report-attachments-editor" }, (html) => {
			if (this._hasAttachments()) {
				this._renderAttachmentsOn(html);
				this._renderButtonsOn(html);
			} else {
				this._renderNoAttachmentOn(html);
			}
		});
	}

	_renderAttachmentsOn(html) {
		html.div(
			{ klass: "attachment-list" },
			this._getAttachments().map((attachment) =>
				html.span(
					{ klass: "attachment" },
					this._attachmentRenderer(attachment),
				),
			),
		);
	}

	_attachmentRenderer(attachment) {
		return (html) => {
			html.div({
				klass: [
					"icon",
					"big_icon",
					`di-${attachment.getFileExtension()}`,
				],
			});

			html.div(
				{ klass: "name" },
				html.a(
					{
						klass: "attachment-link",
						target: "_blank",
						href: attachment.getContentPath(),
					},
					attachment.getFilename(),
				),
			);

			html.a(
				{
					klass: "delete btn btn-xs btn-default",
					"data-test": "delete-attachment",
				},
				trashAltIcon,
				_("Delete"),
			).click(() => {
				this._deleteAttachment(attachment);
			});
		};
	}

	_renderNoAttachmentOn(html) {
		html.div(
			{ klass: "no-attachment" },
			html.h3(_("No attachment yet.")),
			(html) => this._renderButtonsOn(html),
		);
	}

	_renderButtonsOn(html) {
		if (this._hasAllDefaultAttachments()) {
			html.div(
				{ class: "attachment-buttons" },
				this._renderNewAttachmentButtonOn.bind(this),
				this._renderCustomReportAttachmentButtonOn.bind(this),
			);
		} else {
			html.div(
				{ class: "attachment-buttons" },
				this._renderNewAttachmentButtonOn.bind(this),
				this._renderAttachDefaultReportsButtonOn.bind(this),
				this._renderCustomReportAttachmentButtonOn.bind(this),
			);
		}
	}

	_renderNewAttachmentButtonOn(html) {
		const label = this._hasAttachments()
			? _("Attach file")
			: _("Attach a first file");

		html.render(
			new FileUploadButtonWidget({
				label,
				cssClass: "btn btn-primary",
				onUpload: this._onUpload,
				onSuccess: this._addAttachment.bind(this),
				onError: this._onAttachmentUploadFailure.bind(this),
			}),
		);
	}

	_onAttachmentUploadFailure({ error }) {
		notifier.error(error.message);
	}

	_renderCustomReportAttachmentButtonOn(html) {
		html.render(
			new ReportAttachmentButtonWidget({
				reports: this._customReports,
				onReportSelection: async (report) => {
					await this._service.addReportTemplatesAttachments({
						draft: this._report,
						reportTemplateIds: [report.getId()],
					});

					this.update();
				},
			}),
		);
	}

	_renderAttachDefaultReportsButtonOn(html) {
		html.a(
			{ klass: "btn btn-primary" },
			_("Add default attachments"),
		).click(async () => {
			await this._service.updateDraftDefaultAttachments(this._report);
			this.update();
		});
	}

	_hasAllDefaultAttachments() {
		const service = new PeriodReportAttachmentsService();

		return service.reportHasAllDefaultAttachments(this._report);
	}

	_deleteAttachment(attachment) {
		this._report.removeAttachment(attachment);
		this.attachmentsChanged.trigger();

		this.update();
	}

	_addAttachment(file, data) {
		const attachments = data.map(
			(datum) =>
				new PeriodReportAttachment({
					id: datum.id,
					filename: datum.fileName,
					attachmentType:
						PeriodReportAttachment.getUploadedAttachmentType(),
				}),
		);

		this._report.addAttachments(attachments);
		this.attachmentsChanged.trigger();
		this.update();
	}

	_getAttachments() {
		return this._report ? this._report.getAttachments() : [];
	}

	_hasAttachments() {
		return this._getAttachments().length !== 0;
	}
}
